import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { deriveState, formatGameClock, gameClockRemaining } from '@/core';
import { getRepository, type GameRecord } from '@/data';
import { ROUTES } from '@/app/App';
import { Button } from '@/ui';
import { CARD, CHIP, LABEL, periodLabel, presetPeriods } from './format';
import { HeaderTools } from './HeaderTools';
import { BallIcon, ChevronIcon, LogoMark, MiniScore } from './parts';

interface HomeData {
  active?: GameRecord;
  /** "Q2 · 5:23" for the active game. */
  activeStatus?: string;
  last?: GameRecord;
  hasGames: boolean;
}

async function loadHome(t: (k: string, o?: Record<string, unknown>) => string): Promise<HomeData> {
  const repo = getRepository();
  const [active, games] = await Promise.all([repo.activeGame(), repo.listGames(50)]);
  const last = games.find((g) => g.status === 'final');
  let activeStatus: string | undefined;
  if (active) {
    try {
      const state = deriveState(await repo.loadEvents(active.id));
      const clock = formatGameClock(gameClockRemaining(state.clock, Date.now()));
      activeStatus = `${periodLabel(t, state.period, state.rules.periods)} · ${clock}`;
    } catch {
      activeStatus = periodLabel(t, active.period, presetPeriods(active.rulesId));
    }
  }
  return { active, activeStatus, last, hasGames: games.length > 0 };
}

export function HomeScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState<HomeData | null>(null);

  useEffect(() => {
    let alive = true;
    loadHome(t)
      .then((d) => alive && setData(d))
      .catch(() => alive && setData({ hasGames: false }));
    return () => {
      alive = false;
    };
  }, [t]);

  return (
    <div className="gutter mx-auto grid max-w-4xl gap-4 py-3 pb-8">
      <header className="flex min-h-[48px] flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 font-display text-[26px] font-bold uppercase tracking-[0.06em]">
          <LogoMark />
          <span>{t('app.name')}</span>
        </div>
        <HeaderTools />
      </header>

      <main className="grid gap-4 md:grid-cols-[1.1fr_1fr]">
        <section className="relative grid content-start gap-3.5 overflow-hidden rounded-card border border-line bg-surface bg-[radial-gradient(120%_90%_at_100%_0%,rgb(var(--hs-accent)/0.22),transparent_60%)] p-6 md:row-span-3">
          <h1 className="text-balance font-display text-[clamp(38px,9vw,64px)] font-bold uppercase leading-[0.95] [html[lang=ta]_&]:font-tamil [html[lang=ta]_&]:text-[clamp(28px,7vw,44px)] [html[lang=ta]_&]:normal-case [html[lang=ta]_&]:leading-tight">
            {t('home.headline')}
          </h1>
          <p className="max-w-[46ch] text-muted">{t('home.subtitle')}</p>
          <CourtLines />
          <Button variant="primary" block onClick={() => navigate(ROUTES.setup)}>
            {t('home.newGame')}
          </Button>
        </section>

        {data === null ? (
          <p className={`${CARD} text-muted`} aria-busy="true">
            {t('common.loading')}
          </p>
        ) : (
          <>
            {data.active && (
              <section className={`${CARD} grid gap-3`} aria-labelledby="home-active">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h2 id="home-active" className={LABEL}>
                    {t('home.gameInProgress')}
                  </h2>
                  {data.activeStatus && (
                    <span className={`${CHIP} tabular`}>{data.activeStatus}</span>
                  )}
                </div>
                <MiniScore teams={data.active.teams} score={data.active.score} />
                <Button
                  variant="primary"
                  block
                  onClick={() => data.active && navigate(ROUTES.game(data.active.id))}
                >
                  {t('home.resumeGame')}
                </Button>
              </section>
            )}

            {data.last && (
              <section className={`${CARD} grid gap-3`} aria-labelledby="home-last">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h2 id="home-last" className={LABEL}>
                    {t('home.lastGame')}
                  </h2>
                  <span className={CHIP}>{t('game.gameOver')}</span>
                </div>
                <MiniScore teams={data.last.teams} score={data.last.score} />
                <Button block onClick={() => data.last && navigate(ROUTES.report(data.last.id))}>
                  {t('history.openReport')}
                </Button>
              </section>
            )}

            {!data.hasGames && (
              <section className={`${CARD} grid justify-items-center gap-2 py-8 text-center`}>
                <BallIcon className="h-14 w-14 opacity-90" />
                <h2 className="font-display text-2xl font-bold uppercase [html[lang=ta]_&]:font-tamil [html[lang=ta]_&]:text-xl [html[lang=ta]_&]:normal-case">
                  {t('home.emptyTitle')}
                </h2>
                <p className="max-w-[36ch] text-muted">{t('home.emptyBody')}</p>
              </section>
            )}
          </>
        )}

        <nav className="grid gap-2" aria-label={t('app.name')}>
          <NavRow to={ROUTES.history} label={t('home.history')} />
          <NavRow to={ROUTES.teams} label={t('home.teams')} />
          <NavRow to={ROUTES.rules} label={t('home.rules')} />
          <NavRow to={ROUTES.settings} label={t('home.settings')} />
        </nav>
      </main>
    </div>
  );
}

function NavRow({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to}
      className="flex min-h-touch items-center justify-between gap-3 rounded-2xl border border-line bg-surface px-[18px] font-semibold"
    >
      <span>{label}</span>
      <ChevronIcon />
    </Link>
  );
}

/** Decorative half-court lines, as in the prototype. */
function CourtLines() {
  return (
    <svg
      viewBox="0 0 320 80"
      preserveAspectRatio="none"
      aria-hidden="true"
      className="h-20 w-full rounded-2xl border border-line text-line"
    >
      <line x1="160" y1="0" x2="160" y2="80" stroke="currentColor" strokeWidth="2" />
      <circle cx="160" cy="40" r="22" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M0 18h46v44H0M320 18h-46v44h46" fill="none" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}
